import React from "react";
import { FormSection, InputField } from "../../../common/FormComponents";
import {
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import Tooltip from "../../../common/FormComponents/Tooltip";

type BillingAddress = {
  streetAddress: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
};

type BillingAddressSectionProps = {
  billingAddress: BillingAddress;
  onChange: ({
    field,
    value,
  }: {
    field: keyof BillingAddress;
    value: string;
  }) => void;
  useSameAsAddress: boolean;
  onSameAsAddressChange: ({ checked }: { checked: boolean }) => void;
  errors?: Record<string, string>;
};

const provinces = [
  { value: "Alberta", label: "Alberta" },
  { value: "British Columbia", label: "British Columbia" },
  { value: "Manitoba", label: "Manitoba" },
  { value: "New Brunswick", label: "New Brunswick" },
  { value: "Newfoundland and Labrador", label: "Newfoundland and Labrador" },
  { value: "Northwest Territories", label: "Northwest Territories" },
  { value: "Nova Scotia", label: "Nova Scotia" },
  { value: "Nunavut", label: "Nunavut" },
  { value: "Ontario", label: "Ontario" },
  { value: "Prince Edward Island", label: "Prince Edward Island" },
  { value: "Quebec", label: "Quebec" },
  { value: "Saskatchewan", label: "Saskatchewan" },
  { value: "Yukon", label: "Yukon" },
];

const countries = [
  { value: "Canada", label: "Canada" },
  { value: "United States", label: "United States" },
  { value: "Other", label: "Other" },
];

const BillingAddressSection = ({
  billingAddress,
  onChange,
  useSameAsAddress,
  onSameAsAddressChange,
  errors = {},
}: BillingAddressSectionProps) => {
  const handleChange =
    (field: keyof BillingAddress) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field, value: e.target.value });
    };

  const handleSelectChange =
    (field: keyof BillingAddress) => (e: SelectChangeEvent<string>) => {
      onChange({ field, value: e.target.value });
    };

  const handleSameAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSameAsAddressChange({ checked: e.target.checked });
  };

  return (
    <FormSection
      title="Billing Address"
      tooltipText="Enter the company's billing address"
    >
      <div className="mb-4">
        <FormControlLabel
          control={
            <Checkbox
              checked={useSameAsAddress}
              onChange={handleSameAddressChange}
              color="primary"
            />
          }
          label="Same as company address"
        />
      </div>

      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-x-6 ${
          useSameAsAddress ? "opacity-50" : ""
        }`}
      >
        <div className="md:col-span-2">
          <InputField
            id="billing-street-address"
            name="billingStreetAddress"
            label="Street Address"
            value={billingAddress.streetAddress}
            onChange={handleChange("streetAddress")}
            error={!!errors.billingStreetAddress}
            helperText={errors.billingStreetAddress || ""}
            required
            disabled={useSameAsAddress}
            tooltipText="Enter the billing street address including unit/suite number if applicable"
          />
        </div>

        <InputField
          id="billing-city"
          name="billingCity"
          label="City"
          value={billingAddress.city}
          onChange={handleChange("city")}
          error={!!errors.billingCity}
          helperText={errors.billingCity || ""}
          required
          disabled={useSameAsAddress}
          tooltipText="Enter the billing city name"
        />

        <div className="mb-6">
          <div className="flex items-center mb-1">
            <label
              htmlFor="billing-province"
              className="block text-sm font-medium text-gray-900"
            >
              Province/State
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Tooltip content="Select the billing province or state" />
          </div>
          <Select
            id="billing-province"
            name="billingProvince"
            value={billingAddress.province}
            onChange={handleSelectChange("province")}
            displayEmpty
            fullWidth
            error={!!errors.billingProvince}
            disabled={useSameAsAddress}
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 1,
                borderColor: "#E1A731",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d32f2f",
              },
              "& .MuiSelect-select": {
                fontSize: 16,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select a province/state
            </MenuItem>
            {provinces.map((province) => (
              <MenuItem key={province.value} value={province.value}>
                {province.label}
              </MenuItem>
            ))}
          </Select>
          {errors.billingProvince && (
            <p className="text-red-500 text-xs mt-1">
              {errors.billingProvince}
            </p>
          )}
        </div>

        <InputField
          id="billing-postal-code"
          name="billingPostalCode"
          label="Postal/Zip Code"
          value={billingAddress.postalCode}
          onChange={handleChange("postalCode")}
          error={!!errors.billingPostalCode}
          helperText={errors.billingPostalCode || ""}
          required
          disabled={useSameAsAddress}
          tooltipText="Enter the billing postal or zip code"
        />

        <div className="mb-6">
          <div className="flex items-center mb-1">
            <label
              htmlFor="billing-country"
              className="block text-sm font-medium text-gray-900"
            >
              Country
              <span className="text-red-500 ml-1">*</span>
            </label>
            <Tooltip content="Select the billing country" />
          </div>
          <Select
            id="billing-country"
            name="billingCountry"
            value={billingAddress.country}
            onChange={handleSelectChange("country")}
            displayEmpty
            fullWidth
            error={!!errors.billingCountry}
            disabled={useSameAsAddress}
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#B0B5BC",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 1,
                borderColor: "#E1A731",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                borderColor: "#d32f2f",
              },
              "& .MuiSelect-select": {
                fontSize: 16,
              },
            }}
          >
            <MenuItem value="" disabled>
              Select a country
            </MenuItem>
            {countries.map((country) => (
              <MenuItem key={country.value} value={country.value}>
                {country.label}
              </MenuItem>
            ))}
          </Select>
          {errors.billingCountry && (
            <p className="text-red-500 text-xs mt-1">{errors.billingCountry}</p>
          )}
        </div>
      </div>
    </FormSection>
  );
};

export default BillingAddressSection;
